import React, { useEffect, useState } from 'react'
import PageHeader from '../../PageHeader/PageHeader'
import { HitApi } from '../../Api/ApiHit'
import { searchSalonChild } from '../../constant/Constant'
import CustomModal from '../../components/CustomModal/CustumModal';

function RateCard() {
    // Load jsPDF if not already loaded
    useEffect(() => {
        if (!window.jspdf) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            script.async = true;
            document.head.appendChild(script);
        }
    }, []);

    const [services, setServices] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [selectedTemplate, setSelectedTemplate] = useState(1)
    const [showPreview, setShowPreview] = useState(false)

    useEffect(() => {
        getServices()
    }, [])

    const getServices = async () => {
        try {
            setLoading(true)
            setError(null)

            const json = {
                page: 1,
                limit: 50,
                search: {}
            }

            const res = await HitApi(json, searchSalonChild)
            console.log("res", res)

            if (res && res.data) {
                setServices(res.data)
            } else {
                setServices([])
            }
        } catch (err) {
            console.error("Error fetching services:", err)
            setError("Failed to fetch services")
        } finally {
            setLoading(false)
        }
    }

    const groupServicesByParent = () => {
        const grouped = {}
        services.forEach(service => {
            const parentName = service.parentId?.name || 'Other Services'
            if (!grouped[parentName]) {
                grouped[parentName] = []
            }
            grouped[parentName].push(service)
        })
        return grouped
    }

    const templates = [
        {
            id: 1,
            name: 'Classic Elegance',
            description: 'Dark background with gold accents, elegant floral patterns',
            colors: { primary: '#1a1a1a', accent: '#DAA520', text: '#ffffff' },
            style: 'elegant',
            backgroundImage: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?q=80&w=1000&auto=format&fit=crop&ixlib=rb-4.0.3'
        },
        {
            id: 2,
            name: 'Modern Minimal',
            description: 'Clean white background with subtle floral accents',
            colors: { primary: '#ffffff', accent: '#2563eb', text: '#1f2937' },
            style: 'minimal',
            backgroundImage: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?q=80&w=1000&auto=format&fit=crop&ixlib=rb-4.0.3'
        },
        {
            id: 3,
            name: 'Royal Purple',
            description: 'Deep purple with rose gold highlights and luxury florals',
            colors: { primary: '#4c1d95', accent: '#e879f9', text: '#ffffff' },
            style: 'royal',
            backgroundImage: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?q=80&w=1000&auto=format&fit=crop&ixlib=rb-4.0.3'
        },
        {
            id: 4,
            name: 'Luxury Gold',
            description: 'Cream background with rich gold details and vintage florals',
            colors: { primary: '#fef3c7', accent: '#b45309', text: '#1f2937' },
            style: 'luxury',
            backgroundImage: 'https://images.unsplash.com/photo-1525137844086-d5c1d0c1d3d1?q=80&w=1000&auto=format&fit=crop&ixlib=rb-4.0.3'
        }
    ]

    const generatePDF = (templateId) => {
        try {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();
            const template = templates.find(t => t.id === templateId);

            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const margin = 20;

            // Template-specific styling functions
            const applyTemplateStyle = (template) => {
                switch (template.style) {
                    case 'elegant':
                        return applyElegantStyle(doc, pageWidth, pageHeight);
                    case 'minimal':
                        return applyMinimalStyle(doc, pageWidth, pageHeight);
                    case 'royal':
                        return applyRoyalStyle(doc, pageWidth, pageHeight);
                    case 'luxury':
                        return applyLuxuryStyle(doc, pageWidth, pageHeight);
                    default:
                        return applyElegantStyle(doc, pageWidth, pageHeight);
                }
            };

            // Cover Page
            applyTemplateStyle(template);

            // Add services pages
            let yPosition = 50;
            const groupedServices = groupServicesByParent();

            doc.addPage();
            applyTemplateStyle(template);

            Object.entries(groupedServices).forEach(([parentName, serviceList]) => {
                if (yPosition > pageHeight - 80) {
                    doc.addPage();
                    applyTemplateStyle(template);
                    yPosition = 50;
                }

                // Category header
                addCategoryHeader(doc, parentName, yPosition, pageWidth, template);
                yPosition += 30;

                // Services
                serviceList.forEach((service) => {
                    if (yPosition > pageHeight - 40) {
                        doc.addPage();
                        applyTemplateStyle(template);
                        yPosition = 50;
                    }

                    addServiceItem(doc, service, yPosition, pageWidth, margin, template);
                    yPosition += 15;
                });

                yPosition += 20;
            });

            // Thank you page
            doc.addPage();
            addThankYouPage(doc, pageWidth, pageHeight, template);

            const fileName = `${template.name}_Rate_Card_${new Date().toLocaleDateString().replace(/\//g, '_')}.pdf`;
            doc.save(fileName);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    // Template styling functions with floral patterns
    const drawFloralPattern = (doc, pageWidth, pageHeight, style) => {
        // Different patterns for different templates
        switch (style) {
            case 'elegant':
                // Simple elegant dots pattern
                doc.setDrawColor(80, 80, 80);
                doc.setLineWidth(0.2);
                for (let x = 10; x < pageWidth; x += 12) {
                    for (let y = 10; y < pageHeight; y += 12) {
                        doc.circle(x, y, 0.8, 'S');
                        if ((x + y) % 24 === 0) {
                            doc.circle(x + 3, y + 3, 0.5, 'F');
                        }
                    }
                }
                break;

            case 'minimal':
                // Much lighter and subtle pattern for better text visibility
                doc.setDrawColor(240, 240, 240);
                doc.setLineWidth(0.3);
                for (let x = 25; x < pageWidth; x += 35) {
                    for (let y = 25; y < pageHeight; y += 35) {
                        // Very subtle small flowers
                        doc.circle(x, y, 1, 'S');
                        // Tiny petals
                        for (let i = 0; i < 4; i++) {
                            const angle = (i * 90) * Math.PI / 180;
                            const petalX = x + Math.cos(angle) * 1.5;
                            const petalY = y + Math.sin(angle) * 1.5;
                            doc.circle(petalX, petalY, 0.5, 'S');
                        }
                    }
                }
                break;

            case 'royal':
                // Elegant royal pattern
                doc.setDrawColor(120, 70, 180);
                doc.setLineWidth(0.3);
                for (let x = 12; x < pageWidth; x += 20) {
                    for (let y = 12; y < pageHeight; y += 20) {
                        doc.circle(x, y, 2, 'S');
                        for (let i = 0; i < 4; i++) {
                            const angle = (i * 90) * Math.PI / 180;
                            const leafX = x + Math.cos(angle) * 3;
                            const leafY = y + Math.sin(angle) * 3;
                            doc.circle(leafX, leafY, 0.8, 'S');
                        }
                    }
                }
                break;

            case 'luxury':
                // Very subtle pattern for luxury template - better text visibility
                doc.setDrawColor(220, 200, 150);
                doc.setLineWidth(0.2);
                for (let x = 20; x < pageWidth; x += 30) {
                    for (let y = 20; y < pageHeight; y += 30) {
                        doc.circle(x, y, 1, 'S');
                        doc.circle(x + 5, y + 5, 0.5, 'S');
                    }
                }
                break;
        }
    };

    const applyElegantStyle = (doc, pageWidth, pageHeight) => {
        // Dark background
        doc.setFillColor(26, 26, 26);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Add subtle floral pattern
        drawFloralPattern(doc, pageWidth, pageHeight, 'elegant');

        // Cover page elements
        if (doc.internal.getNumberOfPages() === 1) {
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(50);
            doc.setFont('helvetica', 'bold'); // Using Helvetica as Montserrat alternative
            doc.text('Elevate Salon', pageWidth / 2, pageHeight / 2 - 35, { align: 'center' });

            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text('Luxury. Perfection. Affordability.', pageWidth / 2, pageHeight / 2 - 10, { align: 'center' });

            doc.setTextColor(218, 165, 32);
            doc.setFontSize(32);
            doc.setFont('helvetica', 'italic');
            doc.text('Rate Card', pageWidth / 2, pageHeight / 2 + 20, { align: 'center' });
        }
    };

    const applyMinimalStyle = (doc, pageWidth, pageHeight) => {
        // Clean white background
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Add very subtle floral pattern that won't interfere with text
        drawFloralPattern(doc, pageWidth, pageHeight, 'minimal');

        if (doc.internal.getNumberOfPages() === 1) {
            doc.setTextColor(31, 41, 55);
            doc.setFontSize(46); // Increased size
            doc.setFont('helvetica', 'bold'); // Bold Montserrat-like
            doc.text('ELEVATE SALON', pageWidth / 2, pageHeight / 2 - 30, { align: 'center' });

            doc.setTextColor(37, 99, 235);
            doc.setFontSize(18); // Increased size
            doc.setFont('helvetica', 'bold');
            doc.text('Professional Beauty Services', pageWidth / 2, pageHeight / 2, { align: 'center' });

            doc.setTextColor(31, 41, 55);
            doc.setFontSize(28); // Increased size
            doc.setFont('helvetica', 'bold');
            doc.text('RATE CARD', pageWidth / 2, pageHeight / 2 + 40, { align: 'center' });
        }
    };

    const applyRoyalStyle = (doc, pageWidth, pageHeight) => {
        // Deep purple background
        doc.setFillColor(76, 29, 149);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Add royal floral pattern
        drawFloralPattern(doc, pageWidth, pageHeight, 'royal');

        if (doc.internal.getNumberOfPages() === 1) {
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(44);
            doc.setFont('times', 'bold');
            doc.text('Elevate Salon', pageWidth / 2, pageHeight / 2 - 30, { align: 'center' });

            doc.setTextColor(232, 121, 249);
            doc.setFontSize(14);
            doc.text('Royal Beauty Experience', pageWidth / 2, pageHeight / 2, { align: 'center' });

            doc.setTextColor(232, 121, 249);
            doc.setFontSize(28);
            doc.setFont('times', 'italic');
            doc.text('Rate Card', pageWidth / 2, pageHeight / 2 + 35, { align: 'center' });
        }
    };

    const applyLuxuryStyle = (doc, pageWidth, pageHeight) => {
        // Lighter cream background for better text visibility
        doc.setFillColor(250, 248, 240);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Add very subtle luxury pattern
        drawFloralPattern(doc, pageWidth, pageHeight, 'luxury');

        if (doc.internal.getNumberOfPages() === 1) {
            // Darker text for better contrast
            doc.setTextColor(120, 60, 20);
            doc.setFontSize(48);
            doc.setFont('helvetica', 'bold'); // Montserrat-like font
            doc.text('Elevate Salon', pageWidth / 2, pageWidth / 2 - 30, { align: 'center' });

            doc.setTextColor(80, 40, 10);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text('Luxury • Perfection • Affordability', pageWidth / 2, pageHeight / 2, { align: 'center' });

            doc.setTextColor(160, 80, 20);
            doc.setFontSize(34);
            doc.setFont('helvetica', 'italic');
            doc.text('Rate Card', pageWidth / 2, pageHeight / 2 + 35, { align: 'center' });
        }
    };

    const addCategoryHeader = (doc, categoryName, yPosition, pageWidth, template) => {
        const categoryWidth = doc.getTextWidth(categoryName) + 30;
        const categoryX = (pageWidth - categoryWidth) / 2;

        // Set colors based on template
        const accentColor = template.colors.accent;
        const rgb = hexToRgb(accentColor);

        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.roundedRect(categoryX, yPosition - 8, categoryWidth, 16, 8, 8, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(15); // Increased size
        doc.setFont('helvetica', 'bold'); // Bold Helvetica for Montserrat look
        doc.text(categoryName, pageWidth / 2, yPosition + 2, { align: 'center' });
    };

    const addServiceItem = (doc, service, yPosition, pageWidth, margin, template) => {
        const textColor = hexToRgb(template.colors.text);
        doc.setTextColor(textColor.r, textColor.g, textColor.b);

        // Service name with Helvetica (closest to Montserrat)
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(service.name, margin, yPosition);

        // Price with Montserrat-style font (using Helvetica Bold as closest alternative)
        const price = `₹ ${service.price}*`;
        doc.setFont('courier', 'bold'); // Using Courier Bold for Montserrat-like appearance
        doc.setFontSize(12);
        doc.text(price, pageWidth - margin, yPosition, { align: 'right' });
    };

    const addThankYouPage = (doc, pageWidth, pageHeight, template) => {
        // Apply template background with floral pattern
        switch (template.style) {
            case 'elegant':
                applyElegantStyle(doc, pageWidth, pageHeight);
                break;
            case 'minimal':
                applyMinimalStyle(doc, pageWidth, pageHeight);
                break;
            case 'royal':
                applyRoyalStyle(doc, pageWidth, pageHeight);
                break;
            case 'luxury':
                applyLuxuryStyle(doc, pageWidth, pageHeight);
                break;
        }

        const accentColor = hexToRgb(template.colors.accent);
        doc.setTextColor(accentColor.r, accentColor.g, accentColor.b);

        doc.setFontSize(50); // Increased font size
        doc.setFont('helvetica', 'bold'); // Montserrat-like font
        doc.text('Thank You', pageWidth / 2, pageHeight / 2 - 25, { align: 'center' });

        doc.setFontSize(44); // Increased font size
        doc.setFont('helvetica', 'bolditalic');
        doc.text('for Visiting Us', pageWidth / 2, pageHeight / 2 + 25, { align: 'center' });

        // Better text visibility for luxury template
        if (template.style === 'luxury') {
            doc.setTextColor(80, 40, 10); // Darker text for luxury template
        } else {
            const textColor = hexToRgb(template.colors.text);
            doc.setTextColor(textColor.r, textColor.g, textColor.b);
        }

        doc.setFontSize(14); // Increased font size
        doc.setFont('helvetica', 'normal');
        doc.text('Pricing for hair services will vary depending', pageWidth / 2, pageHeight - 45, { align: 'center' });
        doc.text('on hair length and volume.', pageWidth / 2, pageHeight - 30, { align: 'center' });
    };

    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
    };

    const groupedServices = groupServicesByParent();

    return (
        <div className='min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50'>
            <div className='p-6'>
                <PageHeader
                    title={'Rate Card Generator'}
                    description={'Choose a template and generate professional rate cards'}
                />

                {/* Template Selection */}
                <div className="mt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Template</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {templates.map((template) => (
                            <div
                                key={template.id}
                                className={`relative cursor-pointer rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2 ${selectedTemplate === template.id
                                        ? 'ring-4 ring-blue-500 ring-offset-2'
                                        : 'hover:ring-2 hover:ring-gray-300'
                                    }`}
                                onClick={() => setSelectedTemplate(template.id)}
                            >
                                {/* Template Preview */}
                                <div
                                    className="h-48 p-4 flex flex-col justify-center items-center text-center relative overflow-hidden"
                                    style={{
                                        backgroundColor: template.colors.primary,
                                        backgroundImage: template.style === 'elegant'
                                            ? `radial-gradient(circle at 12px 12px, rgba(80,80,80,0.3) 0.8px, transparent 1px), radial-gradient(circle at 36px 36px, rgba(80,80,80,0.2) 0.5px, transparent 1px)`
                                            : template.style === 'minimal'
                                                ? `radial-gradient(circle at 35px 35px, rgba(180,180,180,0.4) 1px, transparent 1px), radial-gradient(circle at 70px 70px, rgba(180,180,180,0.2) 0.5px, transparent 1px)`
                                                : template.style === 'royal'
                                                    ? `radial-gradient(circle at 20px 20px, rgba(120,70,180,0.3) 2px, transparent 2px), radial-gradient(circle at 40px 40px, rgba(120,70,180,0.2) 0.8px, transparent 1px)`
                                                    : template.style === 'luxury'
                                                        ? `radial-gradient(circle at 30px 30px, rgba(220,200,150,0.2) 1px, transparent 1px), radial-gradient(circle at 60px 60px, rgba(220,200,150,0.1) 0.5px, transparent 1px)`
                                                        : '',
                                        backgroundSize: template.style === 'elegant' ? '24px 24px, 48px 48px'
                                            : template.style === 'minimal' ? '35px 35px, 70px 70px'
                                                : template.style === 'royal' ? '40px 40px, 80px 80px'
                                                    : template.style === 'luxury' ? '60px 60px, 120px 120px'
                                                        : ''
                                    }}
                                >
                                    {/* Selection Checkbox */}
                                    <div className="absolute top-3 right-3">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedTemplate === template.id
                                                ? 'bg-blue-500 border-blue-500'
                                                : 'bg-white border-gray-300'
                                            }`}>
                                            {selectedTemplate === template.id && (
                                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>

                                    <h3
                                        className="font-bold mb-2"
                                        style={{
                                            color: template.style === 'luxury' ? '#503214' : template.colors.text, // Darker text for luxury
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                            fontWeight: '700',
                                            fontSize: '20px'
                                        }}
                                    >
                                        Elevate Salon
                                    </h3>
                                    <p
                                        className="text-sm mb-3"
                                        style={{
                                            color: template.style === 'luxury' ? '#503214' : template.colors.text,
                                            opacity: template.style === 'luxury' ? 1 : 0.8,
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                            fontWeight: '700',
                                            fontSize: '14px'
                                        }}
                                    >
                                        Luxury • Perfection
                                    </p>
                                    <div
                                        className="px-4 py-2 rounded-full text-sm font-semibold"
                                        style={{
                                            backgroundColor: template.colors.accent,
                                            color: '#ffffff',
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                            fontWeight: '700'
                                        }}
                                    >
                                        Rate Card
                                    </div>
                                </div>

                                {/* Template Info */}
                                <div className="bg-white p-4">
                                    <h4 className="font-bold text-gray-900">{template.name}</h4>
                                    <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 justify-center">
                        <button
                            onClick={() => setShowPreview(true)}
                            disabled={loading || services.length === 0}
                            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Preview Template
                        </button>

                        <button
                            onClick={() => generatePDF(selectedTemplate)}
                            disabled={loading || services.length === 0}
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Generate PDF
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <span className="ml-4 text-lg">Loading services...</span>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {error}
                            <button
                                onClick={getServices}
                                className="ml-4 underline hover:no-underline font-semibold"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                )}

                {/* Preview Modal using CustomModal */}
                {showPreview && (
                    <CustomModal
                        isOpen={showPreview}
                        onClose={() => setShowPreview(false)}
                        size="5xl"
                        height={700}
                        className="overflow-hidden"
                    >
                        <div className="p-6 h-full flex flex-col">
                            <div className="flex justify-between items-center mb-6 flex-shrink-0">
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Preview: {templates.find(t => t.id === selectedTemplate)?.name}
                                </h3>
                                <button
                                    onClick={() => setShowPreview(false)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-full hover:bg-gray-100"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Preview Content - Scrollable */}
                            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                                {/* Cover Preview */}
                                <div
                                    className="h-96 rounded-lg p-8 flex flex-col justify-center items-center text-center shadow-lg relative overflow-hidden"
                                    style={{
                                        backgroundColor: templates.find(t => t.id === selectedTemplate)?.colors.primary,
                                        // Exact PDF pattern matching
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'elegant' && {
                                            background: `#1a1a1a radial-gradient(circle at 12px 12px, rgba(80,80,80,0.8) 0.8px, transparent 1px)`
                                        }),
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'minimal' && {
                                            background: `#ffffff radial-gradient(circle at 35px 35px, rgba(240,240,240,1) 1px, transparent 1px)`
                                        }),
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'royal' && {
                                            background: `#4c1d95 radial-gradient(circle at 20px 20px, rgba(120,70,180,0.8) 2px, transparent 2px)`
                                        }),
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'luxury' && {
                                            background: `#faf8f0 radial-gradient(circle at 30px 30px, rgba(220,200,150,0.6) 1px, transparent 1px)`
                                        }),
                                        backgroundSize: templates.find(t => t.id === selectedTemplate)?.style === 'elegant' ? '24px 24px'
                                            : templates.find(t => t.id === selectedTemplate)?.style === 'minimal' ? '35px 35px'
                                                : templates.find(t => t.id === selectedTemplate)?.style === 'royal' ? '40px 40px'
                                                    : templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '60px 60px'
                                                        : 'auto'
                                    }}
                                >
                                    <div className="relative z-10">
                                        <h1
                                            className="text-4xl font-bold mb-4"
                                            style={{
                                                color: templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '#783c14' : templates.find(t => t.id === selectedTemplate)?.colors.text,
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                fontWeight: '700'
                                            }}
                                        >
                                            Elevate Salon
                                        </h1>
                                        <p
                                            className="mb-6"
                                            style={{
                                                color: templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '#502814' : templates.find(t => t.id === selectedTemplate)?.colors.text,
                                                opacity: templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? 1 : 0.9,
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                fontWeight: '700'
                                            }}
                                        >
                                            Luxury. Perfection. Affordability.
                                        </p>
                                        <div
                                            className="px-8 py-3 rounded-full text-xl font-bold shadow-lg"
                                            style={{
                                                backgroundColor: templates.find(t => t.id === selectedTemplate)?.colors.accent,
                                                color: '#ffffff',
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                fontWeight: '700'
                                            }}
                                        >
                                            Rate Card
                                        </div>
                                    </div>
                                </div>

                                {/* Services Preview */}
                                <div
                                    className="rounded-lg p-6 shadow-lg relative overflow-hidden"
                                    style={{
                                        backgroundColor: templates.find(t => t.id === selectedTemplate)?.colors.primary,
                                        // Exact same patterns as cover
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'elegant' && {
                                            background: `#1a1a1a radial-gradient(circle at 12px 12px, rgba(80,80,80,0.8) 0.8px, transparent 1px)`
                                        }),
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'minimal' && {
                                            background: `#ffffff radial-gradient(circle at 35px 35px, rgba(240,240,240,1) 1px, transparent 1px)`
                                        }),
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'royal' && {
                                            background: `#4c1d95 radial-gradient(circle at 20px 20px, rgba(120,70,180,0.8) 2px, transparent 2px)`
                                        }),
                                        ...(templates.find(t => t.id === selectedTemplate)?.style === 'luxury' && {
                                            background: `#faf8f0 radial-gradient(circle at 30px 30px, rgba(220,200,150,0.6) 1px, transparent 1px)`
                                        }),
                                        backgroundSize: templates.find(t => t.id === selectedTemplate)?.style === 'elegant' ? '24px 24px'
                                            : templates.find(t => t.id === selectedTemplate)?.style === 'minimal' ? '35px 35px'
                                                : templates.find(t => t.id === selectedTemplate)?.style === 'royal' ? '40px 40px'
                                                    : templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '60px 60px'
                                                        : 'auto'
                                    }}
                                >
                                    <div className="relative z-10">
                                        <h2
                                            className="text-xl font-bold mb-6 text-center"
                                            style={{
                                                color: templates.find(t => t.id === selectedTemplate)?.colors.accent,
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                fontWeight: '700'
                                            }}
                                        >
                                            P R I C E  L I S T
                                        </h2>

                                        {Object.entries(groupedServices).slice(0, 2).map(([parentName, serviceList]) => (
                                            <div key={parentName} className="mb-6">
                                                <div
                                                    className="inline-block px-6 py-2 rounded-full text-center font-bold text-white mb-4 shadow-lg"
                                                    style={{
                                                        backgroundColor: templates.find(t => t.id === selectedTemplate)?.colors.accent,
                                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                        fontWeight: '700'
                                                    }}
                                                >
                                                    {parentName}
                                                </div>

                                                <div className="space-y-1">
                                                    {serviceList.slice(0, 3).map((service, index) => (
                                                        <div key={index} className="flex justify-between items-center py-1">
                                                            <span
                                                                style={{
                                                                    color: templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '#502814' : templates.find(t => t.id === selectedTemplate)?.colors.text,
                                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                                    fontWeight: '700',
                                                                    fontSize: '13px'
                                                                }}
                                                            >
                                                                {service.name}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    color: templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '#502814' : templates.find(t => t.id === selectedTemplate)?.colors.text,
                                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                                    fontWeight: '700',
                                                                    fontSize: '13px'
                                                                }}
                                                            >
                                                                ₹ {service.price}*
                                                            </span>
                                                        </div>
                                                    ))}
                                                    {serviceList.length > 3 && (
                                                        <div
                                                            className="text-center mt-2"
                                                            style={{
                                                                color: templates.find(t => t.id === selectedTemplate)?.style === 'luxury' ? '#502814' : templates.find(t => t.id === selectedTemplate)?.colors.text,
                                                                opacity: 0.8,
                                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                                fontWeight: '700',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            ... and {serviceList.length - 3} more services
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons - Fixed at bottom */}
                            <div className="mt-6 flex justify-center gap-4 flex-shrink-0 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setShowPreview(false);
                                        generatePDF(selectedTemplate);
                                    }}
                                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    Generate This Template
                                </button>
                                <button
                                    onClick={() => setShowPreview(false)}
                                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                                >
                                    Close Preview
                                </button>
                            </div>
                        </div>
                    </CustomModal>
                )}
            </div>
        </div>
    )
}

export default RateCard